import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import config from '../config/config';
import { authFetch } from '../utils/api';
import { buildSbomCandidates } from '../utils/sbom';
import EmptyState from './common/EmptyState';
import Skeleton from './common/Skeleton';

const statusVariant = {
  verified: 'success',
  mismatch: 'danger',
  incomplete: 'warning',
  unknown: 'secondary',
};

export default function SlsaProvenanceView({ repoName, sbomId, sbomName }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [resolvedSbomName, setResolvedSbomName] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!repoName || !sbomId) return;

    let cancelled = false;
    setLoading(true);
    setError('');
    setData(null);
    setResolvedSbomName('');

    async function load() {
      const candidates = buildSbomCandidates(sbomId, sbomName);
      let saw404 = false;

      for (const candidate of candidates) {
        const response = await authFetch(`${config.BASE_URL}/sbom/${encodeURIComponent(repoName)}/${encodeURIComponent(candidate)}/slsa`);
        if (response.ok) {
          const payload = await response.json();
          if (!cancelled) {
            setData(payload);
            setResolvedSbomName(candidate);
          }
          return;
        }
        if (response.status === 404) {
          saw404 = true;
          continue;
        }
        throw new Error(`HTTP ${response.status}`);
      }

      if (saw404) {
        if (!cancelled) setData(null);
        return;
      }

      throw new Error('HTTP 404');
    }

    load()
      .catch((e) => {
        if (!cancelled) setError(e.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [repoName, sbomId, sbomName]);

  const handleUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setUploadError('');

    try {
      const targetSbom = resolvedSbomName || (sbomName && sbomName.endsWith('.json') ? sbomName : `${sbomId}-sbom.json`);
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        `${config.BASE_URL}/sbom/${encodeURIComponent(repoName)}/${encodeURIComponent(targetSbom)}/slsa`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setData(response.data);
      setResolvedSbomName(targetSbom);
    } catch (e) {
      setUploadError(e.response?.data?.error || e.message);
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  if (loading) {
    return (
        <div style={{ padding: '1rem 0' }}>
          <Skeleton.Card />
          <div className="small text-secondary mt-3">{t('slsa.loading', 'Loading SLSA provenance...')}</div>
        </div>
      );
  }

  if (error) {
    return <div className="alert alert-danger">{t('slsa.loadError', 'Failed to load SLSA provenance')}: {error}</div>;
  }

  const verification = data?.verification;

  return (
    <div className="d-flex flex-column gap-4">
      <div className="card shadow-sm">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap">
            <div>
              <h5 className="mb-1">🔐 SLSA provenance</h5>
              <div className="small text-secondary">
                Upload a provenance attestation for the selected SBOM and verify whether its declared subject digest matches the stored artifact.
              </div>
            </div>
            <div className="d-flex gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept=".json"
                style={{ display: 'none' }}
                onChange={handleUpload}
              />
              <button
                className="btn btn-primary btn-sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={!sbomId || uploading}
              >
                {uploading
                  ? t('slsa.uploading', 'Uploading…')
                  : verification
                    ? t('slsa.replaceAttestation', 'Replace attestation')
                    : t('slsa.uploadAttestation', 'Upload attestation')}
              </button>
            </div>
          </div>

          {uploadError && <div className="alert alert-danger mt-3 mb-0">{uploadError}</div>}

          {!verification && !uploadError && (
            <div className="mt-3 mb-0">
              <EmptyState
                icon="🔐"
                title={t('slsa.emptyTitle')}
                description={t('slsa.emptyDescription')}
                size="sm"
              />
            </div>
          )}
        </div>
      </div>

      {verification && (
        <>
          <div className="card shadow-sm">
            <div className="card-body">
              <div className="row g-3">
                <StatCard label="Verification status" value={verification.verificationStatus} badge={statusVariant[verification.verificationStatus] || 'secondary'} />
                <StatCard label="Estimated SLSA level" value={verification.slsaLevel} />
                <StatCard label="Builder" value={verification.builderId || '—'} />
                <StatCard label="Artifact match" value={verification.subjectMatch ? 'Matched' : 'Not matched'} />
              </div>

              <div className="mt-3 p-3 bg-body-secondary rounded small">
                {verification.verificationMessage}
              </div>
            </div>
          </div>

          <div className="row g-4">
            <div className="col-12 col-lg-6">
              <div className="card shadow-sm h-100">
                <div className="card-body">
                  <h6 className="fw-semibold mb-3">Provenance details</h6>
                  <DetailRow label="Predicate type" value={verification.predicateType} />
                  <DetailRow label="Envelope type" value={verification.envelopeType} />
                  <DetailRow label="Build type" value={verification.buildType} />
                  <DetailRow label="Builder ID" value={verification.builderId} />
                  <DetailRow label="Invocation ID" value={verification.invocationId} />
                  <DetailRow label="Source URI" value={verification.sourceUri} mono />
                  <DetailRow label="Source digest" value={verification.sourceDigest} mono />
                  <DetailRow label="Subject name" value={verification.subjectName} />
                  <DetailRow label="Subject digest" value={verification.subjectDigest} mono />
                  <DetailRow label="Matched artifact" value={verification.matchedArtifact} />
                  <DetailRow label="Matched digest" value={verification.matchedDigest} mono />
                  <DetailRow label="Materials count" value={String(verification.materialsCount ?? 0)} />
                  <DetailRow label="Signature count" value={String(verification.signatureCount ?? 0)} />
                  <DetailRow label="Level source" value={verification.levelSource} />
                </div>
              </div>
            </div>

            <div className="col-12 col-lg-6">
              <div className="card shadow-sm h-100">
                <div className="card-body">
                  <h6 className="fw-semibold mb-3">Verification findings</h6>

                  <div className="mb-3">
                    <div className="small fw-semibold text-secondary mb-2">Verified checks</div>
                    {(verification.verifiedChecks || []).length > 0 ? (
                      <ul className="mb-0 ps-3">
                        {verification.verifiedChecks.map((item) => (
                          <li key={item} className="small mb-2">{item}</li>
                        ))}
                      </ul>
                    ) : (
                      <div className="text-secondary small">No verification checks succeeded.</div>
                    )}
                  </div>

                  <div>
                    <div className="small fw-semibold text-secondary mb-2">Current limitations</div>
                    {(verification.limitations || []).length > 0 ? (
                      <ul className="mb-0 ps-3">
                        {verification.limitations.map((item) => (
                          <li key={item} className="small mb-2">{item}</li>
                        ))}
                      </ul>
                    ) : (
                      <div className="text-secondary small">No additional limitations recorded.</div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function StatCard({ label, value, badge }) {
  return (
    <div className="col-6 col-md-3">
      <div className="border rounded p-3 h-100">
        <div className="small text-secondary mb-1">{label}</div>
        {badge ? <span className={`badge bg-${badge}`}>{value}</span> : <div className="fw-bold">{value}</div>}
      </div>
    </div>
  );
}

function DetailRow({ label, value, mono = false }) {
  return (
    <div className="d-flex justify-content-between gap-3 py-2 border-bottom small">
      <span className="text-secondary">{label}</span>
      <span className={mono ? 'font-monospace text-break text-end' : 'text-end'}>{value || '—'}</span>
    </div>
  );
}
